import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class HttpErrorHandlerService {

  constructor(private router: Router) { }

  /**
   * Handle HTTP error status codes that should redirect to access restricted page
   * @param error - The HTTP error response
   * @returns boolean - true if the error was handled, false otherwise
   */
  handleAccessRestrictedErrors(error: HttpErrorResponse): boolean {
    // Handle access restricted status codes (403, 0, 429)
    if (error.status === 403 || error.status === 0 || error.status === 429) {
      const errorCode = `${error.status}`;
      this.router.navigate(['/access-restricted'], { queryParams: { errorCode } });
      return true;
    }
    return false;
  }

  /**
   * Handle HTTP error status codes that should redirect to login page
   * @param error - The HTTP error response
   * @returns boolean - true if the error was handled, false otherwise
   */
  handleAuthenticationErrors(error: HttpErrorResponse): boolean {
    let noRedirect = ['verify2FAChallenge', 'clientAccessToken', 'revenuedIsBatchBusy'];
    if (error.status === 401 && !noRedirect.some(url => error.url?.includes(url))) {
      this.router.navigate(['/login']);
      return true;
    }
    return false;
  }

  /**
   * Comprehensive error handler that checks for all special status codes
   * @param error - The HTTP error response
   * @returns boolean - true if the error was handled, false otherwise
   */
  handleHttpError(error: HttpErrorResponse): boolean {
    // First check for access restricted errors
    if (this.handleAccessRestrictedErrors(error)) {
      return true;
    }

    // Then check for authentication errors
    if (this.handleAuthenticationErrors(error)) {
      return true;
    }

    return false;
  }

  /**
   * RxJS pipe operator that can be used with HTTP calls to handle special status codes
   * Usage: this.http.get('/api/data').pipe(this.handleHttpErrors())
   */
  handleHttpErrors() {
    return catchError((error: HttpErrorResponse) => {
      if (this.handleHttpError(error)) {
        return throwError(error);
      }
      return throwError(error);
    });
  }
}
